import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import { 
  Shield, MessageSquare, UserX, CheckCircle, ArrowRight,
  EyeOff, Lock, Clock
} from 'lucide-react';
import { trackCTAClick } from '@/utils/analytics';

// AEO Schema for Anonymous Chat page
const seoSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Anonymous Chat - Send Messages Without Revealing Your Identity | Privluxe",
  "description": "Start anonymous conversations without sharing your phone number or personal details. Privluxe lets you chat privately with complete identity protection.",
  "mainEntity": {
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is anonymous chat?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Anonymous chat allows you to have conversations without revealing your real identity, phone number, or email to the other person. On Privluxe, you can start private conversations using anonymous invite links."
        }
      },
      {
        "@type": "Question",
        "name": "How do I start an anonymous chat?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Create a Privluxe account, then send an anonymous invite link via SMS, email, or any messaging app. When the other person accepts, you can chat without them knowing your personal contact details."
        }
      },
      {
        "@type": "Question",
        "name": "Is anonymous chat safe?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Privluxe is designed for private communication. Your identity is protected, messages can be set to disappear, and your personal details are never shared with other users."
        }
      }
    ]
  }
};

export default function AnonymousChat() {
  const navigate = useNavigate();

  const handleCTAClick = (location) => {
    trackCTAClick(location, 'Start Anonymous Chat');
    navigate('/login');
  };

  return (
    <>
      <Helmet>
        <title>Anonymous Chat - Send Messages Without Revealing Your Identity | Privluxe</title>
        <meta name="description" content="Start anonymous conversations without sharing your phone number or personal details. Privluxe lets you chat privately with complete identity protection." />
        <meta name="keywords" content="anonymous chat, private messaging, secret chat, anonymous messaging app, chat without phone number, hidden identity chat" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://privluxe.com/anonymous-chat" />
        <script type="application/ld+json">
          {JSON.stringify(seoSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen" style={{ background: '#020617' }}>
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50" style={{ background: 'rgba(2, 6, 23, 0.9)', backdropFilter: 'blur(20px)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <a href="/" className="flex items-center gap-2">
                <Shield className="h-6 w-6" style={{ color: '#7C3AED' }} />
                <span className="text-lg font-bold tracking-[0.2em]" style={{ color: '#E5E7EB' }}>PRIVLUXE</span>
              </a>
              <Button 
                onClick={() => handleCTAClick('navbar_cta')}
                className="text-white text-sm px-6"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
              >
                Start Chatting
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4">
          <div 
            className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, #7C3AED 0%, transparent 70%)' }}
          />
          
          <div className="relative z-10 max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'rgba(124, 58, 237, 0.2)', border: '1px solid rgba(124, 58, 237, 0.3)' }}>
              <EyeOff className="h-4 w-4" style={{ color: '#7C3AED' }} />
              <span className="text-sm" style={{ color: '#E5E7EB' }}>Anonymous Communication</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight" style={{ color: '#E5E7EB' }}>
              What is Anonymous Chat?
            </h1>

            <p className="text-lg mb-8 max-w-3xl" style={{ color: 'rgba(229, 231, 235, 0.85)' }}>
              Anonymous chat allows you to have conversations without revealing your real identity, phone number, or email address to the other person.
            </p>

            <p className="text-base mb-10 max-w-3xl" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
              On Privluxe, you can start private conversations using anonymous invite links. The person you're chatting with will never see your personal contact details unless you choose to share them.
            </p>

            <div className="flex flex-col items-start gap-2">
              <Button 
                onClick={() => handleCTAClick('hero_cta')}
                className="h-14 px-8 text-white text-lg font-semibold"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                data-testid="anon-chat-cta"
              >
                Start Anonymous Chat
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <span className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>No cost. No identity exposure.</span>
            </div>
          </div>
        </section>

        {/* How Anonymous Chat Works */}
        <section className="py-16 px-4" style={{ background: 'rgba(10, 15, 44, 0.5)' }}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#E5E7EB' }}>
              How Anonymous Chat Works on Privluxe
            </h2>

            <div className="space-y-6">
              <div className="flex items-start gap-4 p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-lg font-bold" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', color: 'white' }}>1</div>
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Create Your Private Account</h3>
                  <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Sign up with your email or phone number. This information is used only for your account and is never shown to people you chat with.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-lg font-bold" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', color: 'white' }}>2</div>
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Send an Anonymous Invite</h3>
                  <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Generate an invite link and share it however you want—SMS, email, or any other app. The link doesn't reveal your identity.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-lg font-bold" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)', color: 'white' }}>3</div>
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Chat Anonymously</h3>
                  <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Once they accept your invite, you can start chatting. They see only your username—not your phone number, email, or real name.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Use Anonymous Chat */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#E5E7EB' }}>
              Why Use Anonymous Chat?
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-xl" style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}>
                <UserX className="h-8 w-8 mb-4" style={{ color: '#7C3AED' }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Privacy from the Start</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>You don't need to give out your phone number to have a conversation. Your personal contact info stays private.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(34, 211, 238, 0.1)', border: '1px solid rgba(34, 211, 238, 0.2)' }}>
                <Lock className="h-8 w-8 mb-4" style={{ color: '#22D3EE' }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Control Over Identity</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>You decide what the other person knows about you. Share your identity only when you're comfortable.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(34, 211, 238, 0.1)', border: '1px solid rgba(34, 211, 238, 0.2)' }}>
                <Clock className="h-8 w-8 mb-4" style={{ color: '#22D3EE' }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Disappearing Messages</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Set messages to disappear after they're read. Perfect for sensitive conversations that shouldn't leave a trace.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}>
                <Shield className="h-8 w-8 mb-4" style={{ color: '#7C3AED' }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>Safe Connections</h3>
                <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Chat with new people without worrying about them having your personal information if things don't work out.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4" style={{ background: 'rgba(10, 15, 44, 0.5)' }}>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#E5E7EB' }}>
              Anonymous Chat FAQ
            </h2>

            <div className="space-y-6">
              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Is anonymous chat truly anonymous?</h3>
                <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Yes. On Privluxe, the person you're chatting with cannot see your phone number, email, or any personal details. They only see your chosen username.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>Can I reveal my identity later if I want?</h3>
                <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>Absolutely. You're in control. If you decide to share your name, number, or any other information, you can do so in your conversation. But it's always your choice.</p>
              </div>

              <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#E5E7EB' }}>How is this different from WhatsApp?</h3>
                <p style={{ color: 'rgba(229, 231, 235, 0.7)' }}>On WhatsApp, you must share your phone number to chat with someone. On Privluxe, you don't. Your contact information stays hidden from the people you message.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 text-center" style={{ background: 'rgba(124, 58, 237, 0.1)' }}>
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#E5E7EB' }}>
              Ready to Try Anonymous Chat?
            </h2>
            <p className="text-lg mb-8" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
              Start a private conversation without sharing your phone number or personal details.
            </p>
            <div className="flex flex-col items-center gap-2">
              <Button 
                onClick={() => handleCTAClick('final_cta')}
                className="h-14 px-10 text-white text-lg font-semibold"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
              >
                Start Anonymous Chat
              </Button>
              <span className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>No cost. No identity exposure.</span>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
